import React, { useEffect } from 'react';

interface ConfirmationModalProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  children,
  isOpen,
  onClose,
  title,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  danger = true,
}) => {
  const [isVisible, setIsVisible] = React.useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" 
          onClick={onClose}
          aria-hidden="true"
        />
        
        <div className={`relative w-full max-w-md transform rounded-2xl bg-white p-6 shadow-xl transition-all duration-300 ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
        }`}>
          <div className="text-center">
            <h3 
              id="modal-title"
              className="text-lg font-semibold leading-6 text-gray-900 mb-4"
            >
              {title}
            </h3>
            
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                {children}
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row-reverse justify-center gap-3">
            <button
              type="button"
              onClick={handleConfirm}
              className={`w-full sm:w-auto inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm ${
                danger 
                  ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500' 
                  : 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500'
              } focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-colors`}
            >
              {confirmText}
            </button>
            
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-colors"
            >
              {cancelText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};